import { ErrorAlert } from 'components/ErrorAlert';
import pluralize from 'pluralize';
import { type FC, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useField, useForm } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { colors, sizes } from '../../../resources/tokens';
import type { CampaignFormValues } from '../campaign.types';
import { generateCombinations } from './MVT.util';
import { MVTCombinations } from './MVTCombinations';

const css = stylesheet({
  error: {
    color: colors.error,
  },
  status: {
    left: 0,
    marginLeft: sizes.medium,
    position: 'absolute',
  },
});

export type MVTCombinationsModalProps = {
  onHide: () => void;
  onCancel: () => void;
  onAction?: () => void;
  title: string;
  actionLabel?: string;
  show: boolean;
  hasPreviews: boolean;
};

export const MVTCombinationsModal: FC<MVTCombinationsModalProps> = ({
  onHide,
  onCancel,
  onAction,
  title,
  actionLabel,
  show,
  hasPreviews,
}) => {
  const form = useForm<CampaignFormValues>();
  const combinationsField = useField('campaign.mvtExperimentInfo.combinations');
  const combinations = form.getState().values.campaign.mvtExperimentInfo?.combinations;
  const dimensions = form.getState().values.campaign.mvtExperimentInfo?.dimensions;
  const totalIncluded = combinations?.filter((combination) => !combination.isExcluded).length ?? 0;
  const totalExcluded = (combinations?.length ?? 0) - totalIncluded;
  const isValid = totalIncluded > 1;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (show) {
      const generatedCombinations = generateCombinations(dimensions, combinations);
      combinationsField.input.onChange(generatedCombinations);
    }
  }, [
    show,
  ]);

  return (
    <Modal centered onHide={onHide} scrollable show={show} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isValid && (
          <ErrorAlert
            contextMessage="MVT configuration validation failed"
            error={new Error('Total selected combinations must be 2 or greater to publish')}
          />
        )}
        <MVTCombinations combinations={combinations} hasPreviews={hasPreviews} hideAction={!onAction} total={totalIncluded} />
        <MVTCombinations combinations={combinations} excluded hasPreviews={hasPreviews} hideAction={!onAction} total={totalExcluded} />
      </Modal.Body>
      <Modal.Footer>
        <div className={css.status}>
          {totalIncluded} {pluralize('Combination', totalIncluded)} Selected
        </div>
        <Button onClick={onCancel} variant="link">
          Close
        </Button>
        {onAction && actionLabel && (
          <Button disabled={!isValid} onClick={onAction} variant="primary">
            {form.getState().dirty ? `Save and ${actionLabel}` : actionLabel}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
